/**
 * Handles the case where an authenticated user lands on a tenant URL
 * whose slug doesn't match their `company_tenant_code`.
 *
 * Three real-world cases (the lookup decides which):
 *   1. **Sub-tenant typed**: the user belongs to `eng-dept` (sub of
 *      company `corpbrain-admin`). Their `canonical` is
 *      `corpbrain-admin`. If they typed `/eng-dept/chat`, the slug
 *      resolves to the same company subtree → silently redirect to
 *      `/corpbrain-admin/chat`.
 *
 *   2. **Cross-tenant jump to a REAL tenant**: the URL slug belongs
 *      to a different company. Security event — force logout, then
 *      send the user to that tenant's branded login (the tenant
 *      exists, so the user CAN log in there if they have an account).
 *
 *   3. **Cross-tenant jump to a NON-EXISTENT tenant**: same logout,
 *      but the destination `/${typedSlug}/login` will not paint a
 *      login form — PublicTenantLogin's slug lookup returns
 *      `not_found` and renders TenantNotFound (bare "Not Found"
 *      page, no platform-default login).
 *
 * The discriminator is `lookupTenantSlug` — distinguishes 404 from
 * "found in different company" from "found in same subtree".
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '@modules/auth/services/authService';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { lookupTenantSlug } from './tenantResolver';
import { loginPathForHost } from './paths';

interface Props {
  typedSlug: string;
  canonicalSlug: string;
}

type Decision =
  | { kind: 'pending' }
  | { kind: 'redirect'; to: string }
  | { kind: 'logout_to_login'; to: string };

export function TenantMismatchHandler({ typedSlug, canonicalSlug }: Props) {
  const location = useLocation();
  const [decision, setDecision] = useState<Decision>({ kind: 'pending' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lookup = await lookupTenantSlug(typedSlug);
      if (cancelled) return;

      // Same company subtree? — silent redirect, preserving the rest
      // of the path (anything after the slug) and the search string.
      const sameSubtree =
        lookup.status === 'found' &&
        (lookup.data.company_tenant_code === canonicalSlug ||
         lookup.data.canonical_slug === canonicalSlug);
      if (sameSubtree) {
        const suffix = location.pathname.replace(
          new RegExp('^/' + escapeRegex(typedSlug) + '(?=/|$)'),
          ''
        );
        const target = '/' + canonicalSlug + (suffix || '/') + (location.search || '') + (location.hash || '');
        setDecision({ kind: 'redirect', to: target });
        return;
      }

      // Different company OR unknown slug — force logout, then send
      // the user to `/${typedSlug}/login`:
      //   • If the slug IS a real tenant in another company →
      //     PublicTenantLogin paints that tenant's branded login.
      //   • If the slug doesn't exist → PublicTenantLogin's own
      //     lookup falls into the `not_found` branch and renders
      //     TenantNotFound (bare 404, no login form).
      // Either way the user ends up where they should — without
      // ever landing on the platform-default branded login.
      const safeSlug = typedSlug.replace(/[^a-z0-9-]/gi, '');
      // loginPathForHost() so the empty-slug fallback can't paint the
      // platform slug into a vanity host's visible URL.
      const target = safeSlug ? `/${safeSlug}/login` : loginPathForHost();
      setDecision({ kind: 'logout_to_login', to: target });
    })();
    return () => { cancelled = true; };
  }, [typedSlug, canonicalSlug, location.pathname, location.search, location.hash]);

  // Side effect — fire authService.logout() AFTER render commits when
  // we've decided to log out. Calling it inline during render
  // dispatches AUTH_CHANGE_EVENT which calls setState in every
  // useAuth() instance — a state update during the render phase
  // React forbids. Also avoids re-firing logout on every re-render.
  //
  // This TAB's session only: the person typed another tenant's URL, which
  // ends what this tab was doing there — not their sign-in elsewhere, and
  // not a sign-in this browser remembers for their own tenant.
  useEffect(() => {
    if (decision.kind === 'logout_to_login') authService.endTabSession();
  }, [decision.kind]);

  if (decision.kind === 'pending') return <LoadingScreen />;
  if (decision.kind === 'redirect') return <Navigate to={decision.to} replace />;
  return <Navigate to={decision.to} replace />;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
